import React, { useRef, useState, useEffect, useMemo } from "react";
import styles from "./EventEmail.module.scss";
import { CustomDialog, TextField as TextFieldWrapper } from "lib";
import * as yup from "yup";
import { Formik, Form } from "formik";
import { Checkbox, Menu, MenuItem, ListItemIcon, ListItemText, IconButton } from "@mui/material";
import { EditorState, ContentState, convertToRaw } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import htmlToDraft from "html-to-draftjs";
import draftToHtml from "draftjs-to-html";
import { EventsAPI, UsersAPI } from "api_inbusiness";
import { toast } from "react-toastify";
import { useUser } from "hooks/useUser";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import PeopleIcon from "@mui/icons-material/People";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import FilterListIcon from "@mui/icons-material/FilterList";
import CheckIcon from "@mui/icons-material/Check";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

// User type configuration for display
const USER_TYPES = {
  LEADER: { label: "Lider", className: "leader" },
  MANAGER: { label: "Manager", className: "manager" },
  HOST: { label: "Gazdă", className: "host" },
  MEMBER: { label: "Membru", className: "member" },
  EX_MEMBER: { label: "Ex-Membru", className: "exMember" },
  GUEST: { label: "Invitat", className: "guest" },
  VOLUNTEER: { label: "Voluntar", className: "volunteer" },
};

// Filter options
const FILTER_OPTIONS = [
  { key: "all", label: "Toți", types: null },
  { key: "attended", label: "Prezenți la Eveniment", types: null, eventAttendees: true },
  { key: "members", label: "Membri", types: ["MEMBER", "HOST", "LEADER", "MANAGER"] },
  { key: "exMembers", label: "Foști Membri", types: ["EX_MEMBER"] },
  { key: "guests", label: "Invitați", types: ["GUEST"] },
  { key: "volunteers", label: "Voluntari", types: ["VOLUNTEER"] },
];

// User Card Component
const UserCard = ({ user, isSelected, onToggle, avatarUrl }) => {
  const userType = USER_TYPES[user.type] || { label: user.type, className: "member" };
  const initials = `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase();
  const companyName = user.favoriteCompany?.name || user.companies?.[0]?.name || "";

  return (
    <div 
      className={`${styles.userCard} ${isSelected ? styles.selected : ""}`}
      onClick={() => onToggle(user.id)}
    >
      <Checkbox 
        checked={isSelected}
        className={styles.userCheckbox}
        size="small"
        sx={{
          color: "#cbd5e1",
          "&.Mui-checked": {
            color: "#4A90E2",
          },
        }}
      />
      
      {avatarUrl ? (
        <img src={avatarUrl} alt={user.firstName} className={styles.userAvatar} />
      ) : (
        <div className={styles.userAvatarPlaceholder}>
          {initials}
        </div>
      )}
      
      <div className={styles.userInfo}>
        <div className={styles.userName}>
          {user.lastName} {user.firstName}
        </div>
        <div className={styles.userMeta}>
          <span className={`${styles.userType} ${styles[userType.className]}`}>
            {userType.label}
          </span>
          {companyName && (
            <span className={styles.userCompany}>• {companyName}</span>
          )}
        </div>
      </div>
    </div>
  );
};

// Main EventEmail Component
const EventEmail = ({ open, setOpen = () => {}, title, selectedEvent }) => {
  const { user } = useUser();
  const formRef = useRef();
  
  // State
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [users, setUsers] = useState([]);
  const [userAvatars, setUserAvatars] = useState({});
  const [checkedUsers, setCheckedUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [eventAttendees, setEventAttendees] = useState(null); // null = not loaded, [] = loaded but empty
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);

  // Allowed file types and max size (10MB)
  const ALLOWED_TYPES = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  // Form setup
  const INITIAL_FORM_STATE = { subject: "" };
  const FORM_VALIDATION = yup.object().shape({
    subject: yup
      .string()
      .typeError("Subiectul nu este valid!")
      .trim()
      .min(3, "Subiectul trebuie să conțină cel puțin 3 caractere!")
      .required("Subiectul este obligatoriu!"),
  });

  // Fetch all users
  const getAllUsers = async () => {
    setIsLoading(true);
    try {
      const res = await UsersAPI.getAllAdmin();
      if (res.ok) {
        // Include all users (including current user)
        const allUsers = res.data.content;
        setUsers(allUsers);
        
        // Load avatars for users
        allUsers.forEach(async (u) => {
          if (u.avatarId) {
            try {
              const { data: imgUrl } = await UsersAPI.getNewUserPicture(u.avatarId);
              setUserAvatars((prev) => ({ ...prev, [u.id]: imgUrl }));
            } catch (e) { /* ignore */ }
          }
        });
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
    setIsLoading(false);
  };

  // Fetch event attendees (users who were present)
  const getEventAttendees = async () => {
    if (!selectedEvent?.id) return;
    setIsLoading(true);
    try {
      const res = await EventsAPI.getEventAttendees(selectedEvent.id);
      if (res.ok) {
        setEventAttendees(res.data);
        // Load avatars for attendees
        res.data.forEach(async (u) => {
          if (u.avatarId && !userAvatars[u.id]) {
            try {
              const { data: imgUrl } = await UsersAPI.getNewUserPicture(u.avatarId);
              setUserAvatars((prev) => ({ ...prev, [u.id]: imgUrl }));
            } catch (e) { /* ignore */ }
          }
        });
      }
    } catch (error) {
      console.error("Error fetching attendees:", error);
      setEventAttendees([]);
    }
    setIsLoading(false);
  };

  // Load users when modal opens
  useEffect(() => {
    if (open) {
      getAllUsers();
      setCheckedUsers([]);
      setSearchQuery("");
      setActiveFilter("all");
      setEventAttendees(null); // Reset attendees cache
    }
  }, [open]);

  // Set editor content from event description
  useEffect(() => {
    if (!selectedEvent || users.length <= 0) return;
    const html = selectedEvent.description || "";
    if (html) {
      const blocksFromHtml = htmlToDraft(html);
      const { contentBlocks, entityMap } = blocksFromHtml;
      const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
      setEditorState(EditorState.createWithContent(contentState));
    }
  }, [selectedEvent, users, open]);

  // Load event attendees when filter changes to "attended"
  useEffect(() => {
    if (activeFilter === "attended" && eventAttendees === null) {
      getEventAttendees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter]);

  // Filter users based on search and active filter
  const filteredUsers = useMemo(() => {
    // For "attended" filter, use eventAttendees data
    const filterOption = FILTER_OPTIONS.find((f) => f.key === activeFilter);
    
    let result;
    if (filterOption?.eventAttendees) {
      // Use attendees data instead of all users
      result = eventAttendees || [];
    } else if (filterOption?.types) {
      // Apply type filter
      result = users.filter((u) => filterOption.types.includes(u.type));
    } else {
      // "all" filter - show all users
      result = users;
    }

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((u) =>
        `${u.firstName} ${u.lastName}`.toLowerCase().includes(query) ||
        u.email?.toLowerCase().includes(query) ||
        u.favoriteCompany?.name?.toLowerCase().includes(query) ||
        u.companies?.some((c) => c.name?.toLowerCase().includes(query))
      );
    }

    return result;
  }, [users, eventAttendees, activeFilter, searchQuery]);

  // Get count for each filter
  const filterCounts = useMemo(() => {
    const counts = {};
    FILTER_OPTIONS.forEach((filter) => {
      if (filter.types === null) {
        counts[filter.key] = users.length;
      } else {
        counts[filter.key] = users.filter((u) => filter.types.includes(u.type)).length;
      }
    });
    return counts;
  }, [users]);

  // Handle user selection toggle
  const handleToggleUser = (userId) => {
    setCheckedUsers((prev) => {
      const exists = prev.some((u) => u.userId === userId);
      if (exists) {
        return prev.filter((u) => u.userId !== userId);
      } else {
        return [...prev, { userId }];
      }
    });
  };

  // Handle select all (filtered)
  const handleSelectAll = () => {
    if (checkedUsers.length === filteredUsers.length && filteredUsers.length > 0) {
      // Deselect all filtered
      const filteredIds = new Set(filteredUsers.map((u) => u.id));
      setCheckedUsers((prev) => prev.filter((u) => !filteredIds.has(u.userId)));
    } else {
      // Select all filtered
      const newChecked = filteredUsers.map((u) => ({ userId: u.id }));
      const existingIds = new Set(checkedUsers.map((u) => u.userId));
      const toAdd = newChecked.filter((u) => !existingIds.has(u.userId));
      setCheckedUsers((prev) => [...prev, ...toAdd]);
    }
  };

  // Check if user is selected
  const isUserSelected = (userId) => checkedUsers.some((u) => u.userId === userId);

  // How many filtered users are selected
  const selectedFilteredCount = filteredUsers.filter((u) => isUserSelected(u.id)).length;
  const allFilteredSelected = selectedFilteredCount === filteredUsers.length && filteredUsers.length > 0;

  // File upload handlers
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = [];
    
    files.forEach((file) => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(`${file.name}: Tip de fișier invalid. Acceptăm PDF, DOC, DOCX și imagini.`);
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name}: Fișierul depășește limita de 10MB.`);
        return;
      }
      validFiles.push(file);
    });

    setAttachments((prev) => [...prev, ...validFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Submit handler
  const onSubmitFunc = async (values) => {
    const rawContentState = convertToRaw(editorState.getCurrentContent());
    const htmlContent = draftToHtml(rawContentState);

    if (checkedUsers.length <= 0) {
      toast.error("Trebuie să selectați cel puțin un utilizator!");
      return;
    }

    // Use FormData for multipart upload with attachments
    const formData = new FormData();
    formData.append('subject', values.subject);
    formData.append('message', htmlContent);
    formData.append('eventId', selectedEvent.id);
    formData.append('userIds', JSON.stringify(checkedUsers));
    
    attachments.forEach((file) => {
      formData.append('attachments', file);
    });

    const res = await EventsAPI.sendEmailWithAttachments(formData);
    if (res.ok === true) {
      setOpen(false);
      setCheckedUsers([]);
      setAttachments([]);
      toast.success("Email trimis cu succes!");
    } else {
      toast.error("A intervenit o eroare!");
    }
  };

  const handleSubmit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  };

  return (
    <CustomDialog
      width="100%"
      maxWidth="950px"
      open={open}
      setOpen={setOpen}
      title={title}
      hideButtons={true}
    >
      <div className={styles.modalContainer}>
        {/* Header with counter */}
        <div className={styles.modalHeader}>
          <span style={{ color: "#64748b", fontSize: "0.9rem" }}>
            Selectează destinatarii și compune mesajul
          </span>
          <div className={styles.selectedCounter}>
            <PeopleIcon />
            {checkedUsers.length} selectați
          </div>
        </div>

        <div className={styles.mainContainer}>
          {/* Left Panel - Recipients */}
          <div className={styles.recipientsPanel}>
            {/* Search + Filter Row */}
            <div className={styles.searchRow}>
              <div className={styles.searchContainer}>
                <SearchIcon className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Caută..."
                  className={styles.searchInput}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              {/* Filter Dropdown Button */}
              <button 
                className={styles.filterBtn}
                onClick={(e) => setFilterAnchorEl(e.currentTarget)}
              >
                <FilterListIcon />
                {FILTER_OPTIONS.find(f => f.key === activeFilter)?.label}
              </button>
              <Menu
                anchorEl={filterAnchorEl}
                open={Boolean(filterAnchorEl)}
                onClose={() => setFilterAnchorEl(null)}
                PaperProps={{
                  sx: { borderRadius: '12px', minWidth: '180px', mt: 1 }
                }}
              >
                {FILTER_OPTIONS.map((filter) => (
                  filterCounts[filter.key] > 0 && (
                    <MenuItem 
                      key={filter.key}
                      onClick={() => { setActiveFilter(filter.key); setFilterAnchorEl(null); }}
                      selected={activeFilter === filter.key}
                      sx={{ borderRadius: '8px', mx: 1, my: 0.5 }}
                    >
                      <ListItemText>{filter.label}</ListItemText>
                      <span style={{ color: '#94a3b8', fontSize: '0.8rem', marginLeft: '8px' }}>
                        {filterCounts[filter.key]}
                      </span>
                      {activeFilter === filter.key && (
                        <ListItemIcon sx={{ minWidth: 'auto', ml: 1 }}>
                          <CheckIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                      )}
                    </MenuItem>
                  )
                ))}
              </Menu>
            </div>

            {/* Select All - Compact */}
            <div className={styles.selectAllCompact}>
              <Checkbox
                checked={allFilteredSelected}
                indeterminate={selectedFilteredCount > 0 && !allFilteredSelected}
                onChange={handleSelectAll}
                size="small"
                sx={{
                  padding: '4px',
                  color: "#cbd5e1",
                  "&.Mui-checked, &.MuiCheckbox-indeterminate": {
                    color: "#4A90E2",
                  },
                }}
              />
              <span className={styles.selectAllText}>Selectează toți membrii</span>
              <span className={styles.selectStats}>
                {selectedFilteredCount}/{filteredUsers.length}
              </span>
            </div>

            {/* Users List */}
            <div className={styles.usersList}>
              {filteredUsers.length === 0 ? (
                <div className={styles.noResults}>
                  <SearchOffIcon />
                  <span>Niciun utilizator găsit</span>
                </div>
              ) : (
                filteredUsers.map((u) => (
                  <UserCard
                    key={u.id}
                    user={u}
                    isSelected={isUserSelected(u.id)}
                    onToggle={handleToggleUser}
                    avatarUrl={userAvatars[u.id]}
                  />
                ))
              )}
            </div>
          </div>

          {/* Right Panel - Compose */}
          <div className={styles.composePanel}>
            <div className={styles.panelTitle}>Compune Mesaj</div>

            <Formik
              innerRef={formRef}
              initialValues={INITIAL_FORM_STATE}
              validationSchema={FORM_VALIDATION}
              onSubmit={onSubmitFunc}
            >
              {() => (
                <Form className={styles.form}>
                  <TextFieldWrapper
                    name="subject"
                    label="Subiect *"
                    size="medium"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        "&:hover fieldset": {
                          borderColor: "#4A90E2",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#4A90E2",
                        },
                      },
                    }}
                  />
                  <div className={styles.editorWrapper}>
                    <Editor
                      toolbarClassName="toolbarClassName"
                      wrapperClassName="wrapperClassName"
                      editorClassName="editor-class"
                      editorState={editorState}
                      onEditorStateChange={setEditorState}
                      toolbarStyle={{
                        borderRadius: "0",
                        border: "none",
                        borderBottom: "1px solid #e2e8f0",
                        background: "#f8fafc",
                        padding: "8px",
                      }}
                      editorStyle={{
                        padding: "12px 16px",
                        minHeight: "200px",
                        maxHeight: "250px",
                        overflowY: "auto",
                      }}
                      preserveSelectionOnBlur={true}
                      toolbar={{
                        options: ['inline', 'fontSize', 'blockType', 'list', 'textAlign', 'link', 'emoji'],
                        inline: {
                          options: ['bold', 'italic', 'underline'],
                        },
                        fontSize: {
                          options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30],
                        },
                      }}
                    />
                  </div>

                  {/* Attachments Section - inside email composition */}
                  <div className={styles.attachmentsSection}>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp"
                      multiple
                      style={{ display: 'none' }}
                    />
                    
                    <button 
                      type="button"
                      className={styles.attachBtn}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <AttachFileIcon />
                      Atașează Fișiere
                    </button>

                    {attachments.length > 0 && (
                      <div className={styles.attachmentsList}>
                        {attachments.map((file, index) => (
                          <div key={index} className={styles.attachmentItem}>
                            <InsertDriveFileIcon className={styles.fileIcon} />
                            <div className={styles.fileInfo}>
                              <span className={styles.fileName}>{file.name}</span>
                              <span className={styles.fileSize}>{formatFileSize(file.size)}</span>
                            </div>
                            <IconButton 
                              size="small" 
                              onClick={() => removeAttachment(index)}
                              className={styles.removeBtn}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>

        {/* Footer */}
        <div className={styles.modalFooter}>
          <button 
            className={styles.cancelBtn}
            onClick={() => setOpen(false)}
          >
            Înapoi
          </button>
          <button 
            className={styles.sendBtn}
            onClick={handleSubmit}
            disabled={checkedUsers.length === 0}
          >
            <SendIcon />
            Trimite Email ({checkedUsers.length}){attachments.length > 0 && ` + ${attachments.length} atașamente`}
          </button>
        </div>
      </div>
    </CustomDialog>
  );
};

export default EventEmail;
